import { useCallback } from "react";

import { isAxiosError } from "axios";

import { Checkbox } from "src/base-components/Checkbox";
import { useUpdateCaseTaskCompletion } from "src/cases/queries/cases";
import { CaseDeserialized } from "src/cases/queries/casesTypes";
import { toastActions } from "src/design-system/Toast/utils";
import { logger } from "src/utils/logger";
import { isPreconditionError } from "src/utils/predicates";

export type CaseTask = {
  id: string;
  key: string;
  title: string;
  isCompleted: boolean;
};

type TaskCheckboxProps = {
  task: CaseTask;
  caseData?: CaseDeserialized;
  disabled?: boolean;
};

export const TaskCheckbox: React.FC<TaskCheckboxProps> = ({
  task,
  caseData,
  disabled,
}) => {
  const { mutateAsync: updateTaskCompletion, isPending } =
    useUpdateCaseTaskCompletion(caseData?.data.id);

  const handleToggle = useCallback(
    async (checked: boolean) => {
      if (!caseData) {
        logger.error("Case data is missing");
        return;
      }

      try {
        await updateTaskCompletion({
          taskKey: task.key,
          taskId: task.id,
          isCompleted: checked,
          etag: caseData.data.etag,
        });

        toastActions.success({
          title: checked ? "Task completed" : "Task marked as incomplete",
        });
      } catch (error) {
        logger.error("Error updating task completion:", error);

        if (isPreconditionError(error)) {
          toastActions.failure({
            title: "Failed to update task",
            description:
              "The task or case has been modified elsewhere. Please reload and try again.",
          });
          return;
        }

        if (isAxiosError(error)) {
          toastActions.failure({
            title: "Failed to update task",
            description: error.response?.data?.message,
          });
          return;
        }

        toastActions.failure({
          title: "Failed to update task",
          description: "An unexpected error occurred. Please try again.",
        });
      }
    },
    [task, caseData, updateTaskCompletion],
  );

  return (
    <Checkbox
      checked={task.isCompleted}
      data-loc={`case-task-checkbox-${task.key}`}
      disabled={disabled || isPending}
      onChange={(event) =>
        handleToggle((event.target as HTMLInputElement).checked)
      }
    />
  );
};
